import * as customersService from '@/services/crm/customers'
import * as projectsService from '@/services/pm/projects'

import router from '@/router'

const DEFAULT_QUERY = {
  pagination: {
    total: 25,
    current: 1,
    pageSize: 25,
    pageSizeOptions: ['10', '25', '35', '45', '100'],
    showQuickJumper: true,
    showSizeChanger: true,
  },
  filters: null,
  sorter: { field: 'created_at', order: 'descend' },
}

const DEFAULT_META = {
  total: 25,
}

const DEFAULT_DATA = {
  data: null,
  loading: false,
  query: Object.assign({}, DEFAULT_QUERY),
  meta: Object.assign({}, DEFAULT_META),
}

const DEFAULT_COMP = {
  visible: false,
  loading: false,
  processing: false,
}

/*********************
 * * STORE OBJECT
 *********************/

export default {
  namespaced: true,
  state: {
    // DATA
    customer: Object.assign({}, DEFAULT_DATA),
    customers: Object.assign({}, DEFAULT_DATA),
    histories: Object.assign({}, DEFAULT_DATA),
    projects: Object.assign({}, DEFAULT_DATA),
    // UI
    ui: {
      SELECT_CUSTOMER: Object.assign({}, DEFAULT_COMP),
      CREATE_CUSTOMER: Object.assign({}, DEFAULT_COMP),
      CREATE_PROJECT: Object.assign({}, DEFAULT_COMP),
    },
  },

  getters: {
    // Main data
    data: (state) => (key) => state[key].data,
    loading: (state) => (key) => state[key].loading,
    query: (state) => (key) => state[key].query,
    pagination: (state) => (key) => state[key].query.pagination,
    filters: (state) => (key) => state[key].query.filters,
    sorter: (state) => (key) => state[key].query.sorter,
    meta: (state) => (key) => state[key].meta,
    // Components Data
    UI_VISIBLE: (state) => (key) => state.ui[key].visible,
    UI_LOADING: (state) => (key) => state.ui[key].loading,
    UI_PROCESSING: (state) => (key) => state.ui[key].processing,
  },

  mutations: {
    INIT: (state) => {
      state.customer = state.customers = state.projects = state.tasks = Object.assign({}, DEFAULT_DATA)
      for (const [key, data] of Object.entries(state.ui)) {
        state.ui[key] = Object.assign({}, DEFAULT_COMP)
      }
    },
    UPDATE: (state, { key, data }) => {
      if (state[key]) state[key].data = data
    },
    SET_LOADING: (state, { key, loading }) => {
      if (state[key]) state[key].loading = loading
    },
    START: (state, key) => {
      if (state[key]) state[key].loading = true
    },
    END: (state, key) => {
      if (state[key]) state[key].loading = false
    },
    SET_QUERY: (state, { key, query }) => {
      if (state[key]) state[key].query = query || Object.assign({}, DEFAULT_QUERY)
    },
    SET_PAGINATION: (state, { key, pagination }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.pagination = pagination
      }
    },
    SET_FILTERS: (state, { key, filters }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.filters = filters
      }
    },
    SET_SORTER: (state, { key, sorter }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.sorter = sorter
      }
    },
    SET_META: (state, { key, meta }) => {
      if (meta) {
        if (meta.total) state[key].query.pagination.total = meta.total
        if (state[key]) state[key].meta = meta
      }
    },
    UI_VISIBLE: (state, { key, visible }) => {
      if (state.ui[key]) state.ui[key].visible = visible
    },
    UI_LOADING: (state, { key, loading }) => {
      if (state.ui[key]) state.ui[key].loading = loading
    },
    UI_PROCESSING: (state, { key, processing }) => {
      if (state.ui[key]) state.ui[key].processing = processing
    },
  },
  actions: {
    // initialization
    init({ commit }) {
      commit('INIT')
    },

    // set pagination
    setPagination({ commit }, { key, pagination }) {
      if (pagination && pagination.current) commit('SET_PAGINATION', { key, pagination })
    },
    // set filters
    setFilters({ commit }, { key, filters }) {
      if (filters && Object.keys(filters).length) commit('SET_FILTERS', { key, filters })
    },
    // set sorter
    setSorter({ commit }, { key, sorter }) {
      if (sorter && sorter.field) commit('SET_SORTER', { key, sorter })
    },
    // set all query same time
    updateQuery({ dispatch }, { key, query: { pagination, filters, sorter } }) {
      dispatch('setPagination', { key, pagination })
      dispatch('setFilters', { key, filters })
      dispatch('setSorter', { key, sorter })
      dispatch('fetchAll', key)
    },

    /*********************
     *  CUSTOMERS
     *********************/

    async fetchAll({ dispatch }, key) {
      if (key === 'customers') {
        return await dispatch('fetchCustomers')
      }
      return null
    },

    // navigation handling
    async navigateToList({ dispatch }) {
      dispatch('init')
      dispatch('close', 'CREATE_CUSTOMER')
      if (router.history.current.path !== '/crm/customers') router.push('/crm/customers')
      dispatch('fetchCustomers')
    },

    // data handling
    async fetchCustomers({ commit, state }) {
      commit('START', 'customers')
      const { customers: data, meta } = await customersService.fetchCustomers(state.customers.query)
      if (data) commit('UPDATE', { key: 'customers', data })
      if (meta) commit('SET_META', { key: 'customers', meta })
      commit('END', 'customers')
      return data
    },

    // fetch customer by id
    async fetchCustomer({ commit }, id) {
      commit('START', 'customer')
      if (id) {
        const data = await customersService.fetchCustomer(id)
        if (data) commit('UPDATE', { key: 'customer', data })
        commit('END', 'customer')
        return data
      }
      commit('END', 'customer')
    },

    async fetchProjectsByCustomer({ commit }, id) {
      commit('START', 'projects')
      if (id) {
        const data = await projectsService.fetchProjectsByCustomerId(id)
        if (data) commit('UPDATE', { key: 'projects', data })
        commit('END', 'projects')
        return data
      }
      commit('END', 'projects')
    },

    async createCustomer({ commit }, customer) {
      commit('START', 'customer')
      if (customer) {
        const data = await customersService.createCustomer(customer)
        console.log(data)
        if (data) {
          commit('UPDATE', { key: 'customer', data })
        }
        commit('END', 'customer')
        return data
      }
      commit('END', 'customer')
      return null
    },

    async fetchHistory({ state, commit }) {
      commit('START', 'histories')
      if (state.customer && state.customer.data._id) {
        const data = await customersService.fetchHistory(state.customer.data._id)
        if (data) commit('UPDATE', { key: 'histories', data })
        commit('END', 'histories')
        return data
      }
      return []
    },

    async insertHistory({ state, commit }, content) {
      commit('START', 'histories')
      if (content && state.customer.data._id) {
        const data = await customersService.insertHistory(state.customer.data._id, content)
        if (data) commit('UPDATE', { key: 'histories', data })
        commit('END', 'histories')
        return data
      }
      commit('END', 'histories')
      return []
    },

    async updateCustomer({ commit }, customer) {
      commit('START', 'customer')
      if (customer) {
        const data = await customersService.updateCustomer(customer)
        if (data) commit('UPDATE', { key: 'customer', data })
        commit('END', 'customer')
        return data
      }
      commit('END', 'customer')
      return null
    },

    // Components Handling
    /*********************
     *  COMPONENTS
     *********************/
    UI_VISIBLE({ commit }, { key, visible }) {
      commit('UI_VISIBLE', { key, visible })
    },
    open({ commit }, key) {
      commit('UI_VISIBLE', { key, visible: true })
    },
    close({ commit }, key) {
      commit('UI_VISIBLE', { key, visible: false })
    },
  },
}
